import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAdventLeaderboard } from "../../features/competition/competitionSlice";

const AdventLeaderboard = () => {
  const dispatch = useDispatch();
  const { adventLeaderboard } = useSelector((state) => state.competitions);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await dispatch(getAdventLeaderboard("advent-2025"));
      setIsLoading(false);
    };
    fetchData();
  }, [dispatch]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-[#D32F2F] text-2xl font-bold animate-pulse">
          Loading leaderboard... 🏆
        </div>
      </div>
    );
  }

  const leaderboard = adventLeaderboard?.leaderboard || [];

  const getRankClass = (rank) => {
    if (rank === 1) return "rank-1";
    if (rank === 2) return "rank-2";
    if (rank === 3) return "rank-3";
    return "rank-default";
  };

  const getRankDisplay = (rank) => {
    if (rank === 1) return "1st";
    if (rank === 2) return "2nd";
    if (rank === 3) return "3rd";
    return `${rank}th`;
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="font-['Cabin_Sketch'] text-5xl md:text-6xl text-[#D32F2F] font-bold mb-4 text-shadow-vintage">
          🏆 Champions
        </h2>
        <p className="text-[#3E2723] text-lg md:text-xl font-semibold max-w-3xl mx-auto">
          Complete challenges to earn points and climb the leaderboard.
          <br />
          
        </p>
      </div>

      <div className="overflow-x-auto">
        {/* Header Row */}
        <div className="hidden md:grid grid-cols-[100px_1fr_180px_180px] gap-8 p-6 mb-4 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] text-white rounded-2xl font-extrabold text-base uppercase tracking-wider border-4 border-[#FFD700] shadow-xl">
          <div>Rank</div>
          <div>Participant</div>
          <div className="text-right">Points</div>
          <div className="text-right">Completed</div>
        </div>

        {/* Leaderboard Rows */}
        <div className="space-y-3">
          {leaderboard.map((entry) => {
            const rankClass = getRankClass(entry.rank);

            return (
              <div
                key={entry._id}
                className="grid grid-cols-1 md:grid-cols-[100px_1fr_180px_180px] gap-4 md:gap-8 p-6 bg-white rounded-2xl border-3 border-[#FFF9C4] hover:border-[#FFD700] hover:bg-gradient-to-r hover:from-[#FFF9C4] hover:to-[#FFF9E6] transition-all duration-300 hover:translate-x-2 hover:shadow-lg"
              >
                {/* Rank */}
                <div className="flex items-center justify-center md:justify-start">
                  <span
                    className={`font-['Cabin_Sketch'] text-4xl md:text-5xl font-bold ${
                      rankClass === "rank-1"
                        ? "text-[#FFD700] text-shadow-gold"
                        : rankClass === "rank-2"
                        ? "text-[#90CAF9] text-shadow-silver"
                        : rankClass === "rank-3"
                        ? "text-[#CD7F32] text-shadow-bronze"
                        : "text-[#D32F2F] text-shadow-default"
                    }`}
                  >
                    {getRankDisplay(entry.rank)}
                  </span>
                </div>

                {/* Name */}
                <div className="flex items-center justify-center md:justify-start">
                  <span className="text-[#3E2723] font-bold text-xl md:text-2xl">
                    {entry.displayName}
                  </span>
                </div>

                {/* Points */}
                <div className="flex flex-col items-center md:items-end justify-center">
                  <span className="text-xs md:text-sm text-[#2E7D32] font-bold uppercase tracking-wider mb-1 md:hidden">
                    Points
                  </span>
                  <span className="font-['Cabin_Sketch'] text-3xl md:text-4xl text-[#2E7D32] font-bold">
                    {entry.totalPoints.toLocaleString()}
                  </span>
                </div>

                {/* Challenges Completed */}
                <div className="flex flex-col items-center md:items-end justify-center">
                  <span className="text-xs md:text-sm text-[#2E7D32] font-bold uppercase tracking-wider mb-1 md:hidden">
                    Completed
                  </span>
                  <span className="text-[#3E2723] text-xl md:text-2xl font-bold">
                    {entry.challengesCompleted} ⭐
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {leaderboard.length === 0 && (
        <div className="text-center py-16">
          <div className="text-[#D32F2F] text-2xl font-bold mb-4">
            🎄 No participants yet! 🎄
          </div>
          <p className="text-[#3E2723] text-lg font-semibold">
            Be the first to join and start climbing the leaderboard!
          </p>
        </div>
      )}

      <style jsx>{`
        .text-shadow-vintage {
          text-shadow: 3px 3px 0px #ffd700;
        }

        .text-shadow-gold {
          text-shadow: 3px 3px 0px #d32f2f;
        }

        .text-shadow-silver {
          text-shadow: 2px 2px 0px #2e7d32;
        }

        .text-shadow-bronze {
          text-shadow: 2px 2px 0px #3e2723;
        }

        .text-shadow-default {
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
        }

        .border-3 {
          border-width: 3px;
        }
      `}</style>
    </div>
  );
};

export default AdventLeaderboard;
